import React, {
	type ComponentType,
	type ReactNode,
	type RefObject,
	useCallback,
	useContext,
	useMemo,
} from 'react';
import { type PreloadedEntryPoint, loadEntryPoint } from 'react-relay';
import SkeletonItem from '@atlaskit/menu/skeleton-item';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import type { ModalDialogProps } from '@atlaskit/modal-dialog/types';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@jira/platform__common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type {
	AnyEntryPoint,
	ComponentOfEntryPoint,
	ParamsOfEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@jira/platform__entry-point/src/common/types.tsx';
import {
	InternalEntryPointContainer,
	type InternalEntryPointContainerProps,
} from '@jira/platform__entry-points-internal-container/src/index.tsx';
import useMergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { ModalContext } from '@jira/platform__entry-points-modal-context/src/ModalContext.tsx';
import { useJiraRelayEnvironmentProvider } from '@jira/platform__graphql-relay-environment-provider/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { usePressablePreloadRef } from './usePressablePreloadRef.tsx';

// eslint-disable-next-line jira/export/restrict-types-re-export
export type ModalEntryPoint = AnyEntryPoint;

export type ModalEntryPointPressableTriggerProps<TEntryPoint extends ModalEntryPoint> = {
	children: ({ ref }: { ref: RefObject<HTMLElement> }) => ReactNode;
	entryPoint: TEntryPoint;
	entryPointParams?: keyof ParamsOfEntryPoint<TEntryPoint> extends never
		? undefined
		: ParamsOfEntryPoint<TEntryPoint>;
	entryPointProps?: Omit<
		RuntimePropsOfEntryPoint<TEntryPoint>,
		'onClose' | 'setShouldCloseOnOverlayClick' | 'setShouldCloseOnEscapePress'
	> & {
		onClose?: () => void;
	};
	Fallback?: ComponentType<{ onClose?: () => void }>;
	interactionName?: string;
	title?: ReactNode;
	modalProps?: Omit<ModalDialogProps, 'onClose'> & {
		// A small manual change until we roll out type change in base atlaskit modal package
		// See DSP-21837
		autoFocus?: RefObject<HTMLElement | null | undefined>;
	};
	useInternalModal: boolean;
	hasCloseButton?: boolean;
} & (keyof ParamsOfEntryPoint<TEntryPoint> extends never
	? {}
	: {
			entryPointParams: unknown;
		}) &
	Partial<Pick<InternalEntryPointContainerProps<TEntryPoint>, 'errorAttributes'>> & {
		forwardedRef?: React.Ref<HTMLElement>;
	};

const emptyObject = {};

/**
 * @deprecated Use Modal EntryPoint API instead instead. https://go/modal-entry-point-api
 * */
export const ModalEntryPointPressableTrigger = <TEntryPoint extends ModalEntryPoint>({
	children,
	entryPoint,
	entryPointParams,
	entryPointProps,
	errorAttributes,
	Fallback,
	interactionName,
	title,
	modalProps,
	useInternalModal,
	forwardedRef,
	hasCloseButton,
}: ModalEntryPointPressableTriggerProps<TEntryPoint>): React.ReactNode => {
	const { openModal } = useContext(ModalContext);
	const environmentProvider = useJiraRelayEnvironmentProvider();

	const metricName = useMemo((): string => {
		if (interactionName) {
			return interactionName;
		}

		// @ts-expect-error Need to fix the types on this
		return entryPoint?.root?.getModuleName() ?? 'unknown';
	}, [entryPoint, interactionName]);

	// eslint-disable-next-line jira/ufo/valid-labels
	const pressTracing = usePressTracing(metricName);

	const load = useCallback(
		() =>
			loadEntryPoint(
				environmentProvider,
				entryPoint,
				// @ts-expect-error TS cannot infer that emptyObject is the correct params
				// type for TEntryPoint even when performing an explicit undefined check
				entryPointParams ?? emptyObject,
			),
		[entryPoint, entryPointParams, environmentProvider],
	);

	const onBeforeLoad = useCallback(() => {
		if (interactionName) {
			pressTracing();
		}
	}, [interactionName, pressTracing]);

	const onLoad = useCallback(
		({
			reference: entryPointReference,
			dispose,
		}: {
			reference: PreloadedEntryPoint<ComponentOfEntryPoint<TEntryPoint>>;
			dispose: () => void;
		}) => {
			openModal(
				({
					onClose: closeModal,
					shouldCloseOnOverlayClick,
					shouldCloseOnEscapePress,
					setShouldCloseOnOverlayClick,
					setShouldCloseOnEscapePress,
				}) => {
					const onClose = () => {
						entryPointProps?.onClose?.();
						closeModal();
						dispose();
					};
					const defaultFallbackInner = (
						<>
							<ModalHeader
								hasCloseButton={fg('platform_relay_use_hasclosebutton') && hasCloseButton}
							>
								{title ? (
									<ModalTitle>{title}</ModalTitle>
								) : (
									<Stack grow="fill">
										<SkeletonItem />
									</Stack>
								)}
							</ModalHeader>
							<ModalBody>
								<SkeletonItem />
								<SkeletonItem />
								<SkeletonItem />
							</ModalBody>
							<ModalFooter />
						</>
					);

					const defaultFallback = useInternalModal ? (
						defaultFallbackInner
					) : (
						<Modal
							messageId="modal-entry-point-pressable-trigger.modal.fallback"
							messageType="transactional"
							onClose={closeModal}
							width={modalProps?.width}
							height={modalProps?.height}
						>
							{defaultFallbackInner}
						</Modal>
					);
					const container = (
						<InternalEntryPointContainer<typeof entryPoint>
							entryPointReference={entryPointReference}
							errorAttributes={errorAttributes}
							errorFallback="flag"
							fallback={Fallback ? <Fallback onClose={closeModal} /> : defaultFallback}
							id={metricName}
							placeholderName="modal-entry-point-pressable-trigger-container"
							// @ts-expect-error Some type mismatch, should fix it one day
							runtimeProps={{
								...(entryPointProps ?? emptyObject),
								onClose,
								setShouldCloseOnOverlayClick,
								setShouldCloseOnEscapePress,
								shouldCloseOnOverlayClick,
								shouldCloseOnEscapePress,
							}}
						/>
					);
					return useInternalModal ? (
						<ShortcutScope>
							<Modal
								{...modalProps}
								messageId="modal-entry-point-pressable-trigger.modal"
								messageType="transactional"
								onClose={onClose}
								shouldCloseOnOverlayClick={
									shouldCloseOnOverlayClick ?? modalProps?.shouldCloseOnOverlayClick
								}
								shouldCloseOnEscapePress={
									shouldCloseOnEscapePress ?? modalProps?.shouldCloseOnEscapePress
								}
							>
								{container}
							</Modal>
						</ShortcutScope>
					) : (
						container
					);
				},
			);
		},
		[
			openModal,
			title,
			useInternalModal,
			modalProps,
			errorAttributes,
			Fallback,
			metricName,
			entryPointProps,
			hasCloseButton,
		],
	);

	const triggerRef = usePressablePreloadRef({
		load,
		onBeforeLoad,
		onLoad,
	});

	const mergedRef = useMergeRefs(triggerRef, forwardedRef ?? triggerRef);
	const ref = forwardedRef != null ? mergedRef : triggerRef;
	// oxlint-disable-next-line typescript/consistent-type-assertions
	return children({ ref: ref as unknown as RefObject<HTMLElement> });
};
